import { resolveOrderTrackingProof } from "@/utils/analytics/orderReference";

export const PAYMENT_SETUP_TOKEN_KEY = "reputech_payment_setup_token";
export const PAYMENT_SETUP_TRACKING_URL_KEY = "reputech_payment_setup_tracking_url";
export const PAYMENT_SETUP_COMPLETION_KEY = "reputech_payment_setup_completed";
export const PAYMENT_SETUP_TOKEN_PARAM = "payment_setup_token";
const PAYMENT_SETUP_TOKEN_PATTERN = /^[A-Za-z0-9]{64}$/;

type PaymentSetupStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export type PaymentSetupTokenIntake = {
  hasTokenParam: boolean;
  token: string | null;
  cleanUrl: string;
};

export function consumePaymentSetupTokenFromUrl(
  href: string,
  storage: PaymentSetupStorage,
): PaymentSetupTokenIntake {
  const url = new URL(href);
  const hasTokenParam = url.searchParams.has(PAYMENT_SETUP_TOKEN_PARAM);
  let token: string | null = null;

  if (hasTokenParam) {
    const values = url.searchParams.getAll(PAYMENT_SETUP_TOKEN_PARAM);
    const candidate = values.length === 1 ? values[0] : null;

    if (candidate && PAYMENT_SETUP_TOKEN_PATTERN.test(candidate)) {
      try {
        if (storage.getItem(PAYMENT_SETUP_TOKEN_KEY) !== candidate) {
          storage.removeItem(PAYMENT_SETUP_TRACKING_URL_KEY);
        }
      } catch {
        // Session storage can be unavailable in hardened browser contexts.
      }

      try {
        storage.setItem(PAYMENT_SETUP_TOKEN_KEY, candidate);
        token = candidate;
      } catch {
        clearPaymentSetupToken(storage);
      }
    } else {
      clearPaymentSetupToken(storage);
    }

    url.searchParams.delete(PAYMENT_SETUP_TOKEN_PARAM);
  }

  return {
    hasTokenParam,
    token,
    cleanUrl: `${url.pathname}${url.search}${url.hash}`,
  };
}

export function removeStripeSetupIntentParamsFromUrl(href: string): string {
  const url = new URL(href);
  url.searchParams.delete("setup_intent");
  url.searchParams.delete("setup_intent_client_secret");
  return `${url.pathname}${url.search}${url.hash}`;
}

export function clearPaymentSetupToken(
  storage?: PaymentSetupStorage,
): void {
  try {
    const target = storage ?? window.sessionStorage;
    target.removeItem(PAYMENT_SETUP_TOKEN_KEY);
    target.removeItem(PAYMENT_SETUP_TRACKING_URL_KEY);
  } catch {
    // The token remains tab-scoped and is discarded automatically with the tab.
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export function resolvePaymentSetupToken(result: unknown): string | null {
  if (!isRecord(result)) return null;

  const direct = result.payment_setup_token;
  if (typeof direct === "string" && PAYMENT_SETUP_TOKEN_PATTERN.test(direct)) return direct;

  return isRecord(result.payload)
    ? resolvePaymentSetupToken(result.payload)
    : null;
}

export function persistPaymentSetupToken(result: unknown): void {
  if (typeof window === "undefined") return;

  const token = resolvePaymentSetupToken(result);
  if (!token) return;

  try {
    if (window.sessionStorage.getItem(PAYMENT_SETUP_TOKEN_KEY) !== token) {
      window.sessionStorage.removeItem(PAYMENT_SETUP_TRACKING_URL_KEY);
    }
    window.sessionStorage.setItem(PAYMENT_SETUP_TOKEN_KEY, token);
    const orderTrackingUrl = resolveOrderTrackingProof(result)?.order_tracking_url;
    if (orderTrackingUrl) {
      window.sessionStorage.setItem(PAYMENT_SETUP_TRACKING_URL_KEY, orderTrackingUrl);
    } else {
      window.sessionStorage.removeItem(PAYMENT_SETUP_TRACKING_URL_KEY);
    }
  } catch {
    // The user can still complete card setup if session storage is unavailable.
  }
}

export function readPaymentSetupToken(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const token = window.sessionStorage.getItem(PAYMENT_SETUP_TOKEN_KEY);
    return token && PAYMENT_SETUP_TOKEN_PATTERN.test(token) ? token : null;
  } catch {
    return null;
  }
}

export function readPaymentSetupTrackingUrl(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(PAYMENT_SETUP_TRACKING_URL_KEY);
  } catch {
    return null;
  }
}

export function markPaymentSetupCompletionTracked(token: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const key = `${PAYMENT_SETUP_COMPLETION_KEY}:${token}`;
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, "1");
    return true;
  } catch {
    return true;
  }
}
